import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, FileText, Trash2, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const CONSENT_STORAGE_KEY = "privacy-policy-consent";
const CURRENT_POLICY_VERSION = "2026-06";

interface ConsentRecord {
  version: string;
  acceptedAt: string;
}

export function PrivacySettingsTab() {
  const [consent, setConsent] = useState<ConsentRecord | null>(null);
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as ConsentRecord;
        setConsent(parsed);
        setConsentGiven(true);
      } catch {
        setConsentGiven(false);
      }
    }
  }, []);

  function handleRevokeConsent() {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    setConsent(null);
    setConsentGiven(false);
    toast.info("Consentimento revogado.");
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Privacidade e LGPD</CardTitle>
          </div>
          <CardDescription>
            Gerencie suas preferências de privacidade e consentimento de dados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium">Consentimento LGPD</h4>
                  {consentGiven ? (
                    <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      Consentimento registrado
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      <XCircle className="mr-1 h-3 w-3" />
                      Sem consentimento
                    </Badge>
                  )}
                </div>
                {consent && (
                  <p className="text-xs text-muted-foreground">
                    Versão da política: {consent.version} · Aceito em{" "}
                    {new Date(consent.acceptedAt).toLocaleDateString("pt-BR")}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Versão atual da política: {CURRENT_POLICY_VERSION}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <a
                  href="/privacidade/politica-de-privacidade.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-1"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Ver Política de Privacidade
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href="/privacidade/solicitacao" className="gap-1">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Solicitar direitos LGPD
                </a>
              </Button>
              {consentGiven && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 text-destructive hover:text-destructive"
                  onClick={handleRevokeConsent}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Revogar consentimento
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
